import { mapGetters } from 'vuex'
import Api from '@/api'
import { getOriginalCreatorId } from '@/utils/fileLabels'
import { canViewerEditFile } from '@/utils/filePermissions'
import { sortFilesByRecency, matchFilesForTemplate } from '@/utils/fileMatch'

/**
 * Shared duplicate-file detection used when creating a file, both from a
 * template (TemplatesPage) and as a free-form "New Upload" where matching is by
 * the typed name (NewFileForm). It resolves the current actor context — a
 * tradie creating for themselves, a business admin uploading for a tradie, or a
 * business admin adding a company file — and gathers the files of the same type
 * scoped to what that actor can see.
 *
 * For template-based matching, pass the template ({ id, label }). For name-only
 * matching (no template), pass { id: null, label: '<typed name>' }.
 */
export default {
  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    uploadingForTradieId() {
      const id = localStorage.getItem('tradie_id')
      return id === null || id === 'undefined' ? null : id
    },

    isTradie() {
      return !!this.user && this.user.user_type === 'tradie'
    },

    isBusinessAdminUploadingForTradie() {
      return (
        !!this.user &&
        this.user.user_type === 'business_admin' &&
        !!this.uploadingForTradieId
      )
    },

    isBusinessAdminCompanyUpload() {
      return (
        !!this.user &&
        this.user.user_type === 'business_admin' &&
        !this.uploadingForTradieId
      )
    },

    // Any business-admin upload (for a tradie or a company file). Used by the
    // duplicate list to show the per-company approval status instead of the
    // shared TradiePass verification status.
    isBusinessAdminUpload() {
      return (
        this.isBusinessAdminUploadingForTradie ||
        this.isBusinessAdminCompanyUpload
      )
    },

    existingFilesSubject() {
      if (this.isBusinessAdminUploadingForTradie)
        return 'This tradie already has'
      if (this.isBusinessAdminCompanyUpload) return 'Your company already has'
      return 'You already have'
    },
  },

  methods: {
    /**
     * Gather the files of this type that already exist, scoped to what the
     * current actor can see, sorted most-recent first and editable-first.
     */
    async findExistingFiles(template) {
      let candidates = []
      // Whether the current actor may edit a given matched file in the modal.
      let canEdit = () => true

      if (this.isBusinessAdminUploadingForTradie) {
        // The tradie endpoint already scopes visibility: it returns the
        // tradie's shared (public) files and the company's own (private/
        // company-only) files, and omits the tradie's truly-private files. So
        // everything it returns counts.
        candidates = await this.fetchTradieFiles(this.uploadingForTradieId)
        // A BA can edit both the tradie's shared (public) files and the
        // company's own (private) ones, same rule as everywhere else —
        // see canViewerEditFile / canEditFile in filePermissions.js.
        canEdit = (f) => canViewerEditFile(f, this.user, this.hasPermission)
      } else if (this.isBusinessAdminCompanyUpload) {
        // Company files: the company's own files (owned by a business admin),
        // all editable by the company. The server already scopes to the
        // company's files; the owner check is a defensive backstop.
        const files = await this.fetchMatchingFiles(template)
        candidates = files.filter((f) => {
          const owner = (f && f.user) || (f && f.file && f.file.user)
          return owner && owner.user_type === 'business_admin'
        })
      } else {
        // The server already returns only the tradie's own visible files; the
        // mine/visibility check is a defensive backstop.
        const files = await this.fetchMatchingFiles(template)
        candidates = files.filter((f) => {
          const owner = (f && f.user) || (f && f.file && f.file.user)
          return owner && owner.mine && this.isVisibleToTradie(f)
        })
      }

      return this.decorateAndSort(
        this.matchFilesForTemplate(candidates, template),
        canEdit,
      )
    },

    /**
     * Free-text duplicate check for the no-template "New Upload" flow: gather
     * every file the current actor can see whose name contains the typed text
     * (case-insensitive), across all file types — not scoped to one template.
     * Visibility scoping matches findExistingFiles: a tradie sees their own
     * visible files, a BA-for-tradie sees the tradie's BA-visible files, and a
     * BA company upload sees the company's files.
     */
    async findFilesMatchingName(name) {
      const term = (name || '').trim().toLowerCase()
      if (!term) return []

      const nameContains = (f) =>
        String((f && f.name) || '')
          .toLowerCase()
          .includes(term)

      let candidates = []
      let canEdit = () => true

      if (this.isBusinessAdminUploadingForTradie) {
        const files = await this.fetchTradieFiles(this.uploadingForTradieId)
        candidates = files.filter(nameContains)
        canEdit = (f) => canViewerEditFile(f, this.user, this.hasPermission)
      } else if (this.isBusinessAdminCompanyUpload) {
        // The server search is scoped to the company's files; the owner check
        // and client-side contains are defensive backstops.
        const files = await this.fetchFilesBySearch(term)
        candidates = files.filter((f) => {
          const owner = (f && f.user) || (f && f.file && f.file.user)
          return (
            owner && owner.user_type === 'business_admin' && nameContains(f)
          )
        })
      } else {
        const files = await this.fetchFilesBySearch(term)
        candidates = files.filter((f) => {
          const owner = (f && f.user) || (f && f.file && f.file.user)
          return (
            owner && owner.mine && this.isVisibleToTradie(f) && nameContains(f)
          )
        })
      }

      return this.decorateAndSort(this.sortByRecency(candidates), canEdit)
    },

    /**
     * Tag each file with whether the current actor may edit it, then order
     * editable files first (recency order is preserved within each group, since
     * the input is expected to already be most-recent first).
     */
    decorateAndSort(files, canEdit) {
      return files
        .map((f) => ({ ...f, canEditInModal: canEdit(f) }))
        .sort((a, b) => Number(b.canEditInModal) - Number(a.canEditInModal))
    },

    /**
     * Sort a list of files most-recently-updated first.
     */
    sortByRecency(files) {
      return sortFilesByRecency(files)
    },

    /**
     * Fetch the current actor's files of this template's type. The server
     * filters by template id / name and scopes the result to the actor (a BA
     * gets the company's files; a tradie gets their own visible files), so this
     * is a single request returning only the candidates rather than paginating
     * the whole library.
     */
    async fetchMatchingFiles(template) {
      const params = new URLSearchParams({ all: '1' })
      if (template && template.id != null) {
        params.set('duplicate_template_id', String(template.id))
      }
      if (template && template.label) {
        params.set('duplicate_name', String(template.label))
      }
      const { data } = await Api.get(`file?${params.toString()}`)
      return data && Array.isArray(data.data) ? data.data : []
    },

    /**
     * Fetch the current actor's files whose name contains the given text,
     * across all file types. The server's `search` filter does the substring
     * (LIKE '%term%') match and scopes the result to the actor, so this returns
     * only the relevant files rather than paginating the whole library.
     */
    async fetchFilesBySearch(term) {
      const params = new URLSearchParams({ all: '1' })
      params.set('filter[search]', term)
      const { data } = await Api.get(`file?${params.toString()}`)
      return data && Array.isArray(data.data) ? data.data : []
    },

    /**
     * Fetch the BA-visible files for a given tradie (not paginated).
     */
    async fetchTradieFiles(tradieId) {
      const { data } = await Api.get(`companies/tradie/${tradieId}`)
      return data && Array.isArray(data.files) ? data.files : []
    },

    /**
     * Match a list of files against the chosen template, most-recent first.
     * Match on template id first; otherwise match by name — the template's
     * label exactly, or as a prefix (e.g. "Passport: my old one") so renamed
     * files of the same type are still caught.
     */
    matchFilesForTemplate(files, template) {
      return matchFilesForTemplate(files, template)
    },

    /**
     * Normalised visibility ('public' | 'private' | '') for a file, handling
     * both the full and nested file shapes.
     */
    fileVisibility(file) {
      const raw =
        file && file.visibility != null
          ? file.visibility
          : (file && file.file && file.file.visibility) || ''
      return String(raw).toLowerCase().trim()
    },

    /**
     * A tradie should only be matched against files they can actually see:
     * public files, or private files they created themselves. Private files a
     * business admin uploaded for them are excluded.
     */
    isVisibleToTradie(file) {
      if (this.fileVisibility(file) === 'private') {
        const creatorId = getOriginalCreatorId(file)
        const myId =
          this.user && this.user.id != null ? Number(this.user.id) : null
        return creatorId != null && myId != null && creatorId === myId
      }

      // Public (or unknown) visibility is visible to the tradie.
      return true
    },
  },
}
